import {
  QuizAsset,
  QuizProduct,
  QuizSize,
  SIZE_AMOUNT,
} from './planQuizConfig';
import { QuizAdjustment, QuizExperience, QuizPriority } from './recommend';

/**
 * All user-facing quiz copy, per language — zero copy lives in components.
 * Follows the existing hard-coded-dictionary pattern for pure-UI strings
 * (see NEW_BADGE_LABELS in ../helpers.ts); everything data-driven (asset
 * labels, product names, amounts, prices) still comes from Sanity i18n.
 */

export type QuizOptionCopy = { label: string; subline: string };

export type QuizCopy = {
  /** BCP 47 locale used to format the earning-potential numbers. */
  numberLocale: string;
  entryLabel: string;
  title: string;
  progress: (current: number, total: number) => string;
  back: string;
  close: string;
  q1: {
    question: string;
    sublines: Record<QuizAsset, string>;
  };
  q2: { question: string; options: Record<QuizExperience, QuizOptionCopy> };
  q3: { question: string; options: Record<QuizPriority, QuizOptionCopy> };
  q4: {
    question: string;
    labels: Record<QuizSize, string>;
    subline: (profit: string, keep: string) => string;
  };
  result: {
    title: string;
    subtitle: string;
    why: Record<QuizProduct, string>;
    /** Short product tagline shown under the product name on the result card. */
    taglines: Record<QuizProduct, string>;
    benefits: Record<QuizProduct, string[]>;
    adjustments: Record<QuizAdjustment, string>;
    primaryCta: string;
    secondaryCta: string;
    restart: string;
  };
  // Used once the global Profit Split is set.
  profitSplit: {
    q4Subline: (profit: string, keep: string, split: number) => string;
    boltBenefit: (split: number) => string;
  };
  profitSplitPercent?: number;
};

const BOLT_SPLIT_BENEFIT = 2;

const EN: QuizCopy = {
  numberLocale: 'en-US',
  entryLabel: 'Which one is for me?',
  title: 'Find your product',
  progress: (current, total) => `Question ${current} of ${total}`,
  back: 'Back',
  close: 'Close',
  q1: {
    question: 'What do you want to trade?',
    sublines: {
      cfd_crypto: 'FX pairs, indices, metals + 70 crypto pairs, 24/7',
      futures: 'CME, COMEX, CBOT & NYMEX contracts',
    },
  },
  q2: {
    question: 'Where are you on your prop journey?',
    options: {
      first_challenge: {
        label: 'This would be my first challenge',
        subline: "New to prop firms — we'll keep it simple",
      },
      new_to_prop: {
        label: "I trade, but I'm new to prop firms",
        subline: 'Live or demo experience, first prop account',
      },
      experienced: {
        label: "I've passed challenges before",
        subline: 'You know the drill',
      },
    },
  },
  q3: {
    question: 'What matters most right now?',
    options: {
      lowest_cost: {
        label: 'Lowest cost to start',
        subline: 'The cheapest way into a funded account',
      },
      fastest_payouts: {
        label: 'The fastest path to real payouts',
        subline: 'Fewer phases, withdrawals every 3 days',
      },
      no_repeat_fee: {
        label: 'Not paying again if I fail',
        subline: 'Keep the fee at risk as small as possible',
      },
      skip_evaluation: {
        label: 'Skipping the evaluation entirely',
        subline: 'A funded account from day one',
      },
    },
  },
  q4: {
    question: 'How much capital do you want to trade?',
    labels: {
      '50k': '$50k — Advanced',
      '100k': '$100k — Expert',
      '200k': '$200k — Master',
    },
    subline: (profit, keep) =>
      `A 10% gain ≈ ${profit} profit — you keep ~${keep} (90% split)`,
  },
  result: {
    title: 'Your perfect match',
    subtitle: 'Based on your answers, we recommend:',
    why: {
      core: 'Because you want the most affordable way into a funded account, with time on your side.',
      rapid:
        "Because you want the fastest route to real payouts, with free retries if it doesn't work out.",
      bolt: "Because you'd rather skip the evaluation and start trading funded capital right away.",
    },
    taglines: {
      core: 'the affordable, flexible starter',
      rapid: 'the fast, no-extra-fees path',
      bolt: 'skip everything, trade now',
    },
    benefits: {
      core: [
        'Lowest cost to get started',
        'End-of-day trailing drawdown — room to breathe intraday',
        '2 soft breaches forgiven before a hard breach',
        'Run up to 10 accounts in parallel',
      ],
      rapid: [
        'Payouts every 3 days — the fastest cash-out',
        'Free resets — retry at zero cost if you breach',
        'Free activation — no fee to go funded',
        'Fewest phases — the fastest path to getting funded',
      ],
      bolt: [
        'No challenge — funded from day one',
        'End-of-day trailing drawdown with a generous loss limit',
        'Keep 90% of your profits, payouts every 10 days',
        'Run up to 10 funded accounts in parallel',
      ],
    },
    adjustments: {
      size_fallback_100k:
        "$200k is currently available on Core with CFD + Crypto — we've matched you to $100k.",
      size_fallback_50k:
        "This combination is currently available up to $50k — we've matched you to $50k.",
    },
    primaryCta: 'Get this plan →',
    secondaryCta: 'See full details',
    restart: 'Start over',
  },
  profitSplit: {
    q4Subline: (profit, keep, split) =>
      `A 10% gain ≈ ${profit} profit — you keep up to ${keep} (up to ${split}% split)`,
    boltBenefit: (split) => `Keep up to ${split}% of your profits, payouts every 10 days`,
  },
};

const DE: QuizCopy = {
  numberLocale: 'de-DE',
  entryLabel: 'Hilf mir wählen',
  title: 'Finde deine Challenge',
  progress: (current, total) => `Frage ${current} von ${total}`,
  back: 'Zurück',
  close: 'Schließen',
  q1: {
    question: 'Was möchtest du traden?',
    sublines: {
      cfd_crypto: 'FX-Paare, Indizes, Metalle + 70 Krypto-Paare, 24/7',
      futures: 'CME-, COMEX-, CBOT- & NYMEX-Kontrakte',
    },
  },
  q2: {
    question: 'Wo stehst du auf deiner Prop-Journey?',
    options: {
      first_challenge: {
        label: 'Das wäre meine erste Challenge',
        subline: 'Neu bei Prop Firmen — wir halten es einfach',
      },
      new_to_prop: {
        label: 'Ich trade, bin aber neu bei Prop Firmen',
        subline: 'Live- oder Demo-Erfahrung, erster Prop-Account',
      },
      experienced: {
        label: 'Ich habe schon Challenges bestanden',
        subline: 'Du kennst das Spiel',
      },
    },
  },
  q3: {
    question: 'Was ist dir gerade am wichtigsten?',
    options: {
      lowest_cost: {
        label: 'Günstigster Einstieg',
        subline: 'Der günstigste Weg zu einem Funded-Account',
      },
      fastest_payouts: {
        label: 'Der schnellste Weg zu echten Auszahlungen',
        subline: 'Weniger Phasen, Auszahlung alle 3 Tage',
      },
      no_repeat_fee: {
        label: 'Nicht erneut zahlen, wenn ich scheitere',
        subline: 'Halte die Gebühr im Risiko so klein wie möglich',
      },
      skip_evaluation: {
        label: 'Die Evaluation komplett überspringen',
        subline: 'Ein Funded-Account ab Tag 1',
      },
    },
  },
  q4: {
    question: 'Mit wie viel Kapital möchtest du traden?',
    labels: {
      '50k': '$50k — Advanced',
      '100k': '$100k — Expert',
      '200k': '$200k — Master',
    },
    subline: (profit, keep) =>
      `10% Gewinn ≈ ${profit} — du behältst ~${keep} (90% Split)`,
  },
  result: {
    title: 'Dein perfektes Match',
    subtitle: 'Basierend auf deinen Antworten empfehlen wir:',
    why: {
      core: 'Weil du den günstigsten Weg zu einem Funded-Account willst — mit genügend Zeit.',
      rapid:
        'Weil du den schnellsten Weg zu echten Auszahlungen willst — mit kostenlosen Neuversuchen, falls es nicht klappt.',
      bolt: 'Weil du die Evaluation lieber überspringst und sofort mit Funded-Kapital tradest.',
    },
    taglines: {
      core: 'der günstige, flexible Einstieg',
      rapid: 'der schnelle Weg ohne Extra-Gebühren',
      bolt: 'alles überspringen, sofort traden',
    },
    benefits: {
      core: [
        'Günstigster Einstieg',
        'End-of-Day Trailing Drawdown — mehr Spielraum intraday',
        '2 Soft Breaches erlaubt vor dem Hard Breach',
        'Bis zu 10 Funded-Accounts parallel',
      ],
      rapid: [
        'Auszahlung alle 3 Tage — schnellster Cash-out',
        'Kostenlose Resets — Neuversuch bei Breach zum Nulltarif',
        'Kostenlose Aktivierung — keine Gebühr zum Funded-Account',
        'Wenigste Phasen — schnellster Weg zum Funded-Account',
      ],
      bolt: [
        'Keine Challenge — ab Tag 1 Funded',
        'End-of-Day Trailing Drawdown mit großzügigem Loss Limit',
        'Behalte 90% deiner Gewinne, Auszahlung alle 10 Tage',
        'Bis zu 10 Funded-Accounts parallel',
      ],
    },
    adjustments: {
      size_fallback_100k:
        '$200k gibt es aktuell nur auf Core mit CFD + Crypto — wir haben dich mit $100k gematcht.',
      size_fallback_50k:
        'Diese Kombination gibt es aktuell bis $50k — wir haben dich mit $50k gematcht.',
    },
    primaryCta: 'Diesen Plan holen →',
    secondaryCta: 'Alle Details ansehen',
    restart: 'Neu starten',
  },
  profitSplit: {
    q4Subline: (profit, keep, split) =>
      `10% Gewinn ≈ ${profit} — du behältst bis zu ${keep} (bis zu ${split}% Split)`,
    boltBenefit: (split) => `Behalte bis zu ${split}% deiner Gewinne, Auszahlung alle 10 Tage`,
  },
};

const BR: QuizCopy = {
  numberLocale: 'pt-BR',
  entryLabel: 'Qual é o ideal para mim?',
  title: 'Encontre seu produto',
  progress: (current, total) => `Pergunta ${current} de ${total}`,
  back: 'Voltar',
  close: 'Fechar',
  q1: {
    question: 'O que você quer operar?',
    sublines: {
      cfd_crypto: 'Pares de FX, índices, metais + 70 pares de cripto, 24/7',
      futures: 'Contratos CME, COMEX, CBOT e NYMEX',
    },
  },
  q2: {
    question: 'Em que ponto você está no prop trading?',
    options: {
      first_challenge: {
        label: 'Este seria meu primeiro desafio',
        subline: 'Novo em prop firms — vamos manter tudo simples',
      },
      new_to_prop: {
        label: 'Eu opero, mas sou novo em prop firms',
        subline: 'Experiência em conta real ou demo, primeira conta prop',
      },
      experienced: {
        label: 'Já passei em desafios antes',
        subline: 'Você já sabe como funciona',
      },
    },
  },
  q3: {
    question: 'O que mais importa agora?',
    options: {
      lowest_cost: {
        label: 'O menor custo para começar',
        subline: 'O jeito mais barato de chegar a uma conta financiada',
      },
      fastest_payouts: {
        label: 'O caminho mais rápido para saques reais',
        subline: 'Menos fases, saques a cada 3 dias',
      },
      no_repeat_fee: {
        label: 'Não pagar de novo se eu falhar',
        subline: 'Arriscar a menor taxa possível',
      },
      skip_evaluation: {
        label: 'Pular a avaliação por completo',
        subline: 'Uma conta financiada desde o primeiro dia',
      },
    },
  },
  q4: {
    question: 'Com quanto capital você quer operar?',
    labels: {
      '50k': '$50k — Advanced',
      '100k': '$100k — Expert',
      '200k': '$200k — Master',
    },
    subline: (profit, keep) =>
      `Um ganho de 10% ≈ ${profit} de lucro — você fica com ~${keep} (divisão de 90%)`,
  },
  result: {
    title: 'Seu produto ideal',
    subtitle: 'Com base nas suas respostas, recomendamos:',
    why: {
      core: 'Porque você quer o jeito mais acessível de chegar a uma conta financiada, sem pressa.',
      rapid:
        'Porque você quer o caminho mais rápido para saques reais, com novas tentativas grátis se não der certo.',
      bolt: 'Porque você prefere pular a avaliação e começar a operar capital financiado agora.',
    },
    taglines: {
      core: 'o começo acessível e flexível',
      rapid: 'o caminho rápido, sem taxas extras',
      bolt: 'pule tudo, opere agora',
    },
    benefits: {
      core: [
        'O menor custo para começar',
        'Trailing drawdown de fim de dia — espaço para respirar no intraday',
        '2 soft breaches perdoados antes de um hard breach',
        'Até 10 contas em paralelo',
      ],
      rapid: [
        'Saques a cada 3 dias — o saque mais rápido',
        'Resets grátis — tente de novo sem custo se violar uma regra',
        'Ativação grátis — sem taxa para ficar financiado',
        'Menos fases — o caminho mais rápido para ser financiado',
      ],
      bolt: [
        'Sem desafio — financiado desde o primeiro dia',
        'Trailing drawdown de fim de dia com um limite de perda generoso',
        'Fique com 90% dos seus lucros, saques a cada 10 dias',
        'Até 10 contas financiadas em paralelo',
      ],
    },
    adjustments: {
      size_fallback_100k:
        'No momento, $200k só está disponível no Core com CFD + Crypto — ajustamos sua escolha para $100k.',
      size_fallback_50k:
        'No momento, esta combinação vai até $50k — ajustamos sua escolha para $50k.',
    },
    primaryCta: 'Quero este plano →',
    secondaryCta: 'Ver todos os detalhes',
    restart: 'Recomeçar',
  },
  profitSplit: {
    q4Subline: (profit, keep, split) =>
      `Um ganho de 10% ≈ ${profit} de lucro — você fica com até ${keep} (divisão de até ${split}%)`,
    boltBenefit: (split) => `Fique com até ${split}% dos seus lucros, saques a cada 10 dias`,
  },
};

const COPY: Record<string, QuizCopy> = { en: EN, de: DE, br: BR };

export function resolveQuizCopy(language?: string, profitSplit?: number | null): QuizCopy {
  const copy = (language && COPY[language]) || EN;
  if (profitSplit == null) return copy;
  return {
    ...copy,
    profitSplitPercent: profitSplit,
    q4: {
      ...copy.q4,
      subline: (profit, keep) => copy.profitSplit.q4Subline(profit, keep, profitSplit),
    },
    result: {
      ...copy.result,
      benefits: {
        ...copy.result.benefits,
        bolt: copy.result.benefits.bolt.map((benefit, i) =>
          i === BOLT_SPLIT_BENEFIT ? copy.profitSplit.boltBenefit(profitSplit) : benefit,
        ),
      },
    },
  };
}

/** Q4 earning-potential subline: 10% gain at the profit split, locale-formatted. */
export function sizeSubline(copy: QuizCopy, size: QuizSize): string {
  const profit = SIZE_AMOUNT[size] * 0.1;
  const keep = profit * ((copy.profitSplitPercent ?? 90) / 100);
  const fmt = (n: number) => `$${new Intl.NumberFormat(copy.numberLocale).format(n)}`;
  return copy.q4.subline(fmt(profit), fmt(keep));
}
